import type { Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase-client';
import { clearTenantCache } from '@/lib/tenant';

export const authClient = {
    signUp: {
        email: async ({
            name,
            email,
            password,
        }: {
            name?: string;
            email: string;
            password: string;
        }) => {
            const redirectTo =
                typeof window !== 'undefined'
                    ? `${window.location.origin}/auth/callback`
                    : (import.meta.env
                          .VITE_SUPABASE_EMAIL_REDIRECT_TO as
                          | string
                          | undefined);
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    ...(name ? { data: { full_name: name } } : undefined),
                    ...(redirectTo ? { emailRedirectTo: redirectTo } : undefined),
                },
            });

            if (error) {
                throw new Error(error.message);
            }

            return data;
        },
    },
    signIn: {
        email: async ({
            email,
            password,
        }: {
            email: string;
            password: string;
        }) => {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                throw new Error(error.message);
            }

            return data;
        },
    },
    resendConfirmation: async (email: string) => {
        const redirectTo =
            typeof window !== 'undefined'
                ? `${window.location.origin}/auth/callback`
                : (import.meta.env.VITE_SUPABASE_EMAIL_REDIRECT_TO as
                      | string
                      | undefined);
        const { error } = await supabase.auth.resend({
            type: 'signup',
            email,
            ...(redirectTo ? { options: { emailRedirectTo: redirectTo } } : {}),
        });

        if (error) {
            throw new Error(error.message);
        }
    },
    signOut: async () => {
        clearTenantCache();
        const { error } = await supabase.auth.signOut();
        if (error) {
            throw new Error(error.message);
        }
    },
};

export function useSession() {
    const [session, setSession] = useState<Session | null>(null);
    const [isPending, setIsPending] = useState(true);

    useEffect(() => {
        let isMounted = true;

        supabase.auth
            .getSession()
            .then(({ data }) => {
                if (!isMounted) {
                    return;
                }
                setSession(data.session ?? null);
                setIsPending(false);
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }
                setIsPending(false);
            });

        const { data: listener } = supabase.auth.onAuthStateChange(
            (_event, nextSession) => {
                if (!isMounted) {
                    return;
                }
                setSession(nextSession);
                setIsPending(false);
            },
        );

        return () => {
            isMounted = false;
            listener.subscription.unsubscribe();
        };
    }, []);

    return { data: session, isPending };
}
